import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {loadData} from '../helper/data-loader';
import JSONParser from '@core/scripts/helper/json-parser';
import {buildQueryKeyUrl, rebuildUrl} from '@article-overview/components/hooks/rebuild-url.js';
import {useEffect, useRef} from 'react';

export const usePaginatedArticles = ({
    enabled,
    initialAjaxUrl,
    ajaxUrl,
    currentPage,
    givenSeoOptimizedUrl,
    givenSanitizedUrl,
    onContentUpdated,
    initialModel,
}) => {
    const initialUrlRef = useRef(initialAjaxUrl);

    const mapModelToObject = (modelJson) => {
        const articles = modelJson.articleContent?.articleListPageItems || [];

        return {
            articles,
            page: currentPage,
            filterContent: modelJson.filterContent,
            navigationContent: modelJson.navigationContent,
            actualPageSize: modelJson.articleContent?.actualPageSize,
            shopContent: modelJson.shopContent,
            seoOptimizedUrl: modelJson.seoOptimizedUrl ?? givenSeoOptimizedUrl,
            sanitizedUrl: modelJson.sanitizedUrl ?? givenSanitizedUrl,
            hits: modelJson.hits,
            configurationContent: modelJson.configurationContent,
            model: modelJson,
        };
    };

    const extractQueryData = (result) => {
        const modelJson = JSONParser.tryParseJSON(result.model);
        return mapModelToObject(modelJson);
    };

    const finalAjaxUrl = ajaxUrl || initialAjaxUrl;
    const cleanedAjaxUrl = rebuildUrl(finalAjaxUrl);
    const cleanedInitialUrl = rebuildUrl(initialUrlRef.current);

    const queryKeyAjaxUrl = buildQueryKeyUrl(cleanedAjaxUrl, false);
    const config = {
        queryKey: ['articles', queryKeyAjaxUrl],
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const urlWithPage = rebuildUrl(finalAjaxUrl);

            if (urlWithPage === cleanedInitialUrl && enabled) {
                const initialData = mapModelToObject(initialModel);
                if (initialData) {
                    return initialData;
                }
            }
            const result = await loadData(urlWithPage);
            if (result) {
                return extractQueryData(result);
            }

            return {
                articles: [],
                page: currentPage,
            };
        },
        enabled: enabled && !!finalAjaxUrl,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000,
    };

    const prevModelRef = useRef();

    useEffect(() => {
        prevModelRef.current = undefined;
    }, [cleanedAjaxUrl]);

    const query = useQuery(config);
    useEffect(() => {
        if (!enabled) {
            return;
        }

        if (query.data && onContentUpdated) {
            const model = query.data.model;
            if (model && model !== prevModelRef.current) {
                prevModelRef.current = model;
                onContentUpdated({model});
            }
        }
    }, [query.data, enabled]);
    return query;
};
